/**
 * BlockedPublishDialog — the "documented v1 follow-up" behind the publish
 * gate: when `publishReportRemote` returns 412 with `blockingFindings`, the
 * user used to get a bare toast count and no path forward. This dialog lists
 * the actual blockers and routes to the Audit tab pre-filtered to the report
 * (`?tab=audit&report=<id>` — the deep link ReportQualityAuditSection and the
 * findings ledger both consume), so "publish blocked" lands directly on the
 * per-report fix queue.
 *
 * Deliberately NO override-token UI here: `issuePublishOverride` is a
 * platform-admin action; non-admins resolve the findings instead.
 */
import { ShieldAlert } from 'lucide-react';
import type { FullAuditFinding } from '../../../lib/audit/runFullAudit';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';

export interface BlockedPublishDialogProps {
  /** Null = closed; non-null = the 412 payload's blocking findings. */
  findings: FullAuditFinding[] | null;
  onClose: () => void;
  /** Navigate to `/sites/{siteId}?tab=audit&report={reportId}`. */
  onReviewInAudit: () => void;
}

/** Reader-language severity labels — the raw enum ('block'/'warn') is an
 *  internal contract, not copy. Matches the Audit tab's vocabulary. */
const SEVERITY_LABEL: Record<string, string> = {
  block: 'Must resolve',
  warn: 'Warning',
  info: 'Note',
};

function severityTone(severity: FullAuditFinding['severity']): string {
  switch (severity) {
    case 'block':
      return 'bg-danger/10 text-danger border-danger/30';
    case 'warn':
      return 'bg-warning/10 text-warning border-warning/30';
    default:
      return 'bg-muted text-text-muted border-border';
  }
}

export function BlockedPublishDialog({ findings, onClose, onReviewInAudit }: BlockedPublishDialogProps) {
  const open = findings !== null;
  const list = findings ?? [];
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) onClose(); }}>
      <DialogContent
        size="md"
        className="bg-background p-6"
        data-testid="blocked-publish-dialog"
        aria-labelledby="blocked-publish-title"
      >
        <DialogHeader>
          <DialogTitle id="blocked-publish-title" className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-danger" aria-hidden />
            Publish blocked — {list.length} issue{list.length === 1 ? '' : 's'} to resolve
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-text-muted">
          The publish gate re-audits the exact revision being shared. These findings
          must be resolved (or validated as correct) before the report can go public.
        </p>
        <ul className="max-h-72 space-y-1.5 overflow-y-auto" data-testid="blocked-publish-list">
          {list.map((f, i) => (
            <li
              key={`${f.detectorId}:${f.kind}:${i}`}
              className="flex items-start gap-2 rounded-md border border-border px-2.5 py-2"
            >
              <span
                className={`mt-0.5 shrink-0 rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${severityTone(f.severity)}`}
              >
                {SEVERITY_LABEL[f.severity] ?? f.severity}
              </span>
              <span className="min-w-0 text-xs text-text-primary">
                {f.message ?? f.kind}
              </span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} data-testid="blocked-publish-close">
            Close
          </Button>
          <Button type="button" onClick={onReviewInAudit} data-testid="blocked-publish-review">
            Review in Audit tab
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
